import React from 'react';
import KeywordHighlighter from './KeywordHighlighter';
import { Info } from 'lucide-react';

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Strip HTML tags so snippets render as clean plain text. */
const stripHtml = (str) => {
  if (!str) return '';
  return String(str).replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
};

// ── ResultItem ────────────────────────────────────────────────────────────────

const ResultItem = ({ result, highlightTerm, onShowDetails, isSampleHighlightingEnabled }) => {
  const { source, display_group, other_keywords, inline_details } = result;

  // Prefer the new 'details' key; fall back to 'inline_details' for older data
  const details = result.details || inline_details || {};

  // "Referred" badge: show when External Laboratory or Sending Lab Details is set
  const externalLab = details?.labUseOnly?.['External Laboratory'];
  const sendingLab  = details?.labUseOnly?.['Sending Lab Details'];
  const isReferred  = Boolean(externalLab || sendingLab);

  // Per-lab badge derived from keywords or source divergence
  let labName = '';
  const labMatch = (other_keywords || '').match(/\(Labs: (.*?)\)/);
  if (labMatch && labMatch[1]) {
    labName = labMatch[1];
  } else if (source && display_group && source !== display_group) {
    labName = source.replace(/\s*\(Sonic\)\s*|\s*\(Sonic Group\)\s*/g, '');
  }

  const isRealLink = result.link && result.link !== '#';
  const hasDetails = result.has_inline_details;

  // AKA / Specimen for Row 2
  const aka      = stripHtml(
    details?.generalInfo?.['Alternative Names'] ||
    details?.generalInfo?.['Synonyms'] ||
    details?.generalInfo?.['Alternate Names'] ||
    details?.generalInfo?.['Also Known As'] ||
    ''
  );
  const specimen = stripHtml(
    details?.specimenCollection?.['Specimen Types'] ||
    details?.specimenCollection?.['Specimen types'] ||
    details?.specimenCollection?.['Specimen Type'] ||
    result.snippet ||
    ''
  );

  const handleRowClick = () => {
    if (hasDetails) onShowDetails(result);
  };

  return (
    <div
      className={`result-row flex flex-col px-5 py-3 transition-all duration-150 ${
        hasDetails ? 'cursor-pointer hover:bg-indigo-500/10' : ''
      }`}
      style={{ borderTop: '1px solid var(--border-primary)' }}
      onClick={handleRowClick}
    >
      {/* ── Row 1: Title + badges ──────────────────────────────────────── */}
      <div className="flex items-center gap-2 min-w-0">
        {/* Title */}
        <h4 className="text-[13px] font-medium truncate flex-1 min-w-0" style={{ color: 'var(--text-primary)' }}>
          {isRealLink ? (
            <a
              href={result.link}
              target="_blank"
              rel="noopener noreferrer"
              referrerPolicy="no-referrer"
              className="hover:text-indigo-300 transition-colors duration-150"
              onClick={(e) => e.stopPropagation()}
            >
              <KeywordHighlighter
                text={result.title}
                searchTerm={highlightTerm}
                enableSampleHighlighting={isSampleHighlightingEnabled}
              />
            </a>
          ) : (
            <KeywordHighlighter
              text={result.title}
              searchTerm={highlightTerm}
              enableSampleHighlighting={isSampleHighlightingEnabled}
            />
          )}
        </h4>

        {/* "Referred" badge */}
        {isReferred && (
          <span
            className="text-[9px] tracking-wider font-bold uppercase px-1.5 py-0.5 rounded shrink-0"
            style={{
              color: '#a5b4fc',
              backgroundColor: 'rgba(99,102,241,0.12)',
              border: '1px solid rgba(99,102,241,0.25)',
            }}
          >
            Referred
          </span>
        )}

        {/* Lab name badge */}
        {labName && (
          <span
            className="text-[10px] tracking-wide px-1.5 py-0.5 rounded shrink-0"
            style={{
              color: 'var(--text-muted)',
              backgroundColor: 'var(--bg-elevated)',
              border: '1px solid var(--border-secondary)',
            }}
          >
            {labName}
          </span>
        )}

        {/* Info icon */}
        {hasDetails && (
          <div className="shrink-0" style={{ color: 'var(--text-faint)' }}>
            <Info size={16} />
          </div>
        )}
      </div>

      {/* ── Row 2: AKA / Specimen grid ────────────────────────────────── */}
      {(aka || specimen) && (
        <div className="grid grid-cols-2 gap-4 mt-1">
          {/* AKA */}
          <div className="flex items-baseline gap-1.5 min-w-0">
            <span
              className="text-[10px] uppercase tracking-wider shrink-0 font-semibold"
              style={{ color: 'var(--text-faint)' }}
            >
              AKA
            </span>
            <span
              className="text-[11px] truncate"
              style={{ color: 'var(--text-muted)' }}
              title={aka}
            >
              {aka || '—'}
            </span>
          </div>

          {/* Specimen */}
          <div className="flex items-baseline gap-1.5 min-w-0">
            <span
              className="text-[10px] uppercase tracking-wider shrink-0 font-semibold"
              style={{ color: 'var(--text-faint)' }}
            >
              Specimen
            </span>
            <span
              className="text-[11px] truncate"
              style={{ color: 'var(--text-muted)' }}
              title={specimen}
            >
              {specimen || '—'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResultItem;